import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import {
  Target,
  Layers,
  LayoutGrid,
  Beaker,
  PlayCircle,
  Wrench,
  CheckCircle2,
} from "lucide-react";
import SEO from "@/components/SEO";
import Breadcrumbs from "@/components/Breadcrumbs";
import Honeypot from "@/components/Honeypot";
import { siteConfig } from "@/config/siteConfig";
import { submitLead, baseMeta } from "@/lib/api";
import { trackLeadSubmit } from "@/lib/analytics";
import TurnstileWidget, { useSubmitBlockedByTurnstile } from "@/components/TurnstileWidget";

const SECTIONS = [
  {
    key: "assessment",
    Icon: Target,
    title: "Application Assessment",
    body:
      "Every enquiry begins with a short assessment: machine make and model, substrate, current ink or cartridge, print requirement, production speed and any existing problem. This shapes the technical direction of our response.",
  },
  {
    key: "substrate",
    Icon: Layers,
    title: "Substrate Review",
    body:
      "Substrate compatibility often matters more than product name — porous board, non-porous film, foil, PET, PVC and coated stocks each behave differently. Our review starts with the surface you print on.",
  },
  {
    key: "selection",
    Icon: LayoutGrid,
    title: "Product Selection",
    body:
      "Recommendations are made on the basis of the assessment. Where more than one product could be relevant, we identify candidates and outline what needs to be verified through evaluation.",
  },
  {
    key: "sample",
    Icon: Beaker,
    title: "Sample Evaluation",
    body:
      "Selected products are made available as structured samples for controlled evaluation on your line. Sample quantities and formats are agreed against the review outcome.",
  },
  {
    key: "trial",
    Icon: PlayCircle,
    title: "Production Trial",
    body:
      "Once a sample has performed acceptably, a full-line production trial is arranged before any regular commercial supply is set up.",
  },
  {
    key: "troubleshooting",
    Icon: Wrench,
    title: "Troubleshooting",
    body:
      "For existing installations, our team supports line-side troubleshooting for adhesion, drying, print quality and cartridge performance — working with your engineers to isolate the root cause.",
  },
];

const initialForm = {
  contactName: "",
  company: "",
  country: "",
  email: "",
  phone: "",
  machineBrand: "",
  machineModel: "",
  currentInk: "",
  substrate: "",
  issue: "",
};

const Field = ({ label, name, type = "text", value, onChange, required }) => {
  const id = `ts-${name}`;
  const common =
    "w-full border border-[var(--border)] rounded-[4px] px-3 py-2.5 text-[14px] text-[var(--charcoal)] bg-white focus:border-[var(--pharma-blue)] focus:outline-none focus:ring-1 focus:ring-[var(--pharma-blue)]";
  return (
    <div>
      <label
        htmlFor={id}
        className="block text-[13px] font-medium text-[var(--charcoal)] mb-1.5"
      >
        {label} {required && <span className="text-[var(--pharma-blue)]">*</span>}
      </label>
      {type === "textarea" ? (
        <textarea
          id={id}
          name={name}
          value={value}
          onChange={onChange}
          required={required}
          rows={4}
          className={common}
          data-testid={`ts-${name}`}
        />
      ) : (
        <input
          id={id}
          name={name}
          type={type}
          value={value}
          onChange={onChange}
          required={required}
          className={common}
          data-testid={`ts-${name}`}
          autoComplete="off"
        />
      )}
    </div>
  );
};

const TechnicalSupport = () => {
  const path = "/technical-support";
  const canonical = `${siteConfig.DOMAIN}${path}`;
  const schema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: "Technical Support",
    description:
      "Application-focused technical support for pharmaceutical printing and coding requirements — from application assessment through to production trial and regular supply.",
    url: canonical,
  };

  const [form, setForm] = useState(initialForm);
  const [hp, setHp] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  const [captchaToken, setCaptchaToken] = useState("");
  const captchaBlocks = useSubmitBlockedByTurnstile(captchaToken);
  const [submitting, setSubmitting] = useState(false);
  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
  const onSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    const res = await submitLead({
      type: "TECHNICAL_ENQUIRY",
      source_page: "/technical-support",
      contact: {
        name: form.contactName,
        company: form.company,
        email: form.email,
        country: form.country,
        phone: form.phone || null,
      },
      attribution: {},
      meta: baseMeta(),
      payload: {
        machine_brand: form.machineBrand || null,
        machine_model: form.machineModel || null,
        current_ink: form.currentInk || null,
        substrate: form.substrate || null,
        issue_or_requirement: form.issue,
      },
      _hp: hp,
      turnstile_token: captchaToken,
    });
    setSubmitting(false);
    if (res.ok) {
      trackLeadSubmit("technical");
      setSubmitted(true);
    }
    else if (res.status === 403) setError("Verification failed. Please retry the challenge.");
    else if (res.status === 429) setError("You are submitting too frequently. Please try again in a minute.");
    else if (res.status === 422) setError("Please check the required fields and try again.");
    else setError("Something went wrong. Please try again shortly.");
  };

  return (
    <>
      <SEO
        rawTitle={`Technical Support & Application Review | ${siteConfig.COMPANY_NAME}`}
        description="Application-focused technical support for pharmaceutical printing and coding — substrate review, sample evaluation, production trial, line-side troubleshooting."
        path={path}
        extraSchema={schema}
      />
      <Helmet>
      </Helmet>

      <div className="mx-auto max-w-[1240px] px-6 lg:px-10 pt-8 lg:pt-10">
        <Breadcrumbs
          items={[
            { label: "Home", to: "/" },
            { label: "Technical Support" },
          ]}
        />
      </div>

      <section
        className="mx-auto max-w-[1240px] px-6 lg:px-10 pt-8 lg:pt-10 pb-8"
        data-testid="ts-hero"
      >
        <p className="eyebrow">Technical Support</p>
        <h1
          className="font-heading mt-4 text-[32px] leading-tight lg:text-[46px] lg:leading-[1.05] font-semibold text-[var(--navy)] max-w-4xl"
          data-testid="ts-h1"
        >
          Technical Support Begins With Understanding the Application
        </h1>
        <div className="mt-6 space-y-4 text-[16px] leading-relaxed text-[var(--charcoal)] max-w-3xl">
          <p>
            Product recommendations depend upon the combination of printing
            process, equipment, substrate, production speed and operating
            conditions. Every application is slightly different, and this
            informs the way we respond to enquiries.
          </p>
          <p>
            Our approach is to review the application first, then recommend a
            candidate product for structured evaluation before regular supply
            is arranged.
          </p>
        </div>
      </section>

      {/* Six sections */}
      <section
        className="mx-auto max-w-[1240px] px-6 lg:px-10 py-12 lg:py-16"
        data-testid="ts-sections"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {SECTIONS.map(({ key, Icon, title, body }) => (
            <article
              key={key}
              className="border border-[var(--border)] rounded-[4px] p-7 bg-white"
              data-testid={`ts-section-${key}`}
            >
              <Icon
                strokeWidth={1.5}
                style={{ width: 32, height: 32, color: "var(--pharma-blue)" }}
                aria-hidden="true"
              />
              <h2 className="font-heading mt-5 text-[19px] font-semibold text-[var(--navy)]">
                {title}
              </h2>
              <p className="mt-3 text-[15px] leading-relaxed text-[var(--slate)]">
                {body}
              </p>
            </article>
          ))}
        </div>
      </section>

      {/* Technical enquiry form */}
      <section
        className="bg-[var(--bg-alt)]"
        data-testid="ts-form-section"
      >
        <div className="mx-auto max-w-[1240px] px-6 lg:px-10 py-16 lg:py-20">
          <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)] gap-10 lg:gap-16 items-start">
            <div>
              <p className="eyebrow">Technical Enquiry</p>
              <h2 className="font-heading mt-4 text-2xl lg:text-3xl font-semibold text-[var(--navy)]">
                Share the details of your application
              </h2>
              <p className="mt-4 text-[15.5px] leading-relaxed text-[var(--charcoal)]">
                Our technical team will review your enquiry and respond with a
                recommendation, questions, or a request for further detail.
              </p>
              <p className="mt-4 text-[13px] italic text-[var(--slate)]">
                Product suitability is confirmed through application review.
                We do not make regulatory approval claims from this form.
              </p>
            </div>

            {submitted ? (
              <div
                className="border border-[var(--border)] rounded-[4px] p-8 bg-white"
                data-testid="ts-success"
                role="status"
              >
                <CheckCircle2
                  strokeWidth={1.5}
                  style={{ width: 40, height: 40, color: "var(--pharma-blue)" }}
                  aria-hidden="true"
                />
                <p className="font-heading mt-4 text-[19px] font-semibold text-[var(--navy)]">
                  Thank you.
                </p>
                <p className="mt-3 text-[14.5px] text-[var(--charcoal)]">
                  A member of our technical team will review your enquiry.
                </p>
              </div>
            ) : (
              <form
                onSubmit={onSubmit}
                className="border border-[var(--border)] rounded-[4px] p-6 lg:p-8 bg-white"
                data-testid="ts-form"
                noValidate
              >
                {error && (
                  <div className="mb-4 border border-[var(--pharma-blue)] rounded-[4px] p-3 bg-[var(--bg-alt)] text-[13.5px] text-[var(--charcoal)]" data-testid="ts-error">
                    {error}
                  </div>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Field label="Contact Name" name="contactName" required value={form.contactName} onChange={onChange} />
                  <Field label="Company" name="company" required value={form.company} onChange={onChange} />
                  <Field label="Country" name="country" required value={form.country} onChange={onChange} />
                  <Field label="Business Email" name="email" type="email" required value={form.email} onChange={onChange} />
                  <Field label="Phone / WhatsApp" name="phone" value={form.phone} onChange={onChange} />
                  <Field label="Machine Brand" name="machineBrand" value={form.machineBrand} onChange={onChange} />
                  <Field label="Machine Model" name="machineModel" value={form.machineModel} onChange={onChange} />
                  <Field label="Current Ink / Cartridge" name="currentInk" value={form.currentInk} onChange={onChange} />
                  <div className="sm:col-span-2">
                    <Field label="Substrate" name="substrate" value={form.substrate} onChange={onChange} />
                  </div>
                  <div className="sm:col-span-2">
                    <Field
                      label="Describe the Issue or Requirement"
                      name="issue"
                      type="textarea"
                      required
                      value={form.issue}
                      onChange={onChange}
                    />
                  </div>
                </div>
                <div className="mt-8">
                  <TurnstileWidget onToken={setCaptchaToken} action="technical_support" testid="ts-turnstile" />
                  <button
                    type="submit"
                    disabled={submitting || captchaBlocks}
                    className="btn-primary text-sm disabled:opacity-70"
                    data-testid="ts-submit"
                  >
                    {submitting ? "Submitting…" : "Submit Technical Enquiry"}
                  </button>
                </div>
                <Honeypot value={hp} onChange={(e) => setHp(e.target.value)} />
              </form>
            )}
          </div>
        </div>
      </section>
    </>
  );
};

export default TechnicalSupport;
